import { sameProtocolTreeId } from './protocolTreeIds';

export type ProtocolSectionTreeNode = {
  _id: string;
  checklistSectionId?: string;
  parentSectionId: string | null;
};

export type ProtocolItemSectionTreeNode = {
  _id: string;
  templateChecklistItem:
    | string
    | {
        _id?: string;
        parentSectionId?: string | null;
      };
};

export type ChecklistTreeNode = {
  _id: string;
  parentSectionId: string | null;
};

function collectDescendantIds(
  nodes: Array<{ _id: string; parentSectionId: string | null }>,
  rootId: string
): string[] {
  const collectedIds: string[] = [rootId];
  const pendingIds = [rootId];

  while (pendingIds.length > 0) {
    const currentId = pendingIds.pop();
    if (!currentId) {
      continue;
    }

    nodes.forEach((node) => {
      if (
        !sameProtocolTreeId(node.parentSectionId, currentId) ||
        collectedIds.some((id) => sameProtocolTreeId(id, node._id))
      ) {
        return;
      }

      collectedIds.push(node._id);
      pendingIds.push(node._id);
    });
  }

  return collectedIds;
}

function parentSectionIdFromProtocolItem(
  protocolItem: ProtocolItemSectionTreeNode,
  checklistItems: ChecklistTreeNode[]
): string | null | undefined {
  const templateChecklistItem = protocolItem.templateChecklistItem;
  if (typeof templateChecklistItem !== 'string') {
    return templateChecklistItem.parentSectionId;
  }

  return checklistItems.find((item) =>
    sameProtocolTreeId(item._id, templateChecklistItem)
  )?.parentSectionId;
}

function collectProtocolItemIdsForChecklistSectionTree(params: {
  checklistSectionId: string;
  protocol: ProtocolItemSectionTreeNode[];
  checklistItems: ChecklistTreeNode[];
  checklistSections: ChecklistTreeNode[];
}): string[] {
  const checklistSectionIds = collectDescendantIds(
    params.checklistSections,
    params.checklistSectionId
  );

  return params.protocol
    .filter((protocolItem) => {
      const parentSectionId = parentSectionIdFromProtocolItem(
        protocolItem,
        params.checklistItems
      );
      return checklistSectionIds.some((sectionId) =>
        sameProtocolTreeId(sectionId, parentSectionId)
      );
    })
    .map((protocolItem) => protocolItem._id);
}

export function collectProtocolItemIdsInSectionTree(params: {
  protocolSectionId: string;
  protocolSections: ProtocolSectionTreeNode[];
  protocol: ProtocolItemSectionTreeNode[];
  checklistItems?: ChecklistTreeNode[];
  checklistSections?: ChecklistTreeNode[];
}): string[] {
  const rootProtocolSection = params.protocolSections.find(
    (section) =>
      sameProtocolTreeId(section._id, params.protocolSectionId) ||
      sameProtocolTreeId(section.checklistSectionId, params.protocolSectionId)
  );
  if (!rootProtocolSection) {
    if ((params.checklistSections ?? []).length === 0) {
      return [];
    }

    return collectProtocolItemIdsForChecklistSectionTree({
      checklistSectionId: params.protocolSectionId,
      protocol: params.protocol,
      checklistItems: params.checklistItems ?? [],
      checklistSections: params.checklistSections ?? [],
    });
  }

  const protocolSectionIds = collectDescendantIds(
    params.protocolSections,
    rootProtocolSection._id
  );

  const checklistSectionIds = protocolSectionIds.flatMap((protocolSectionId) => {
    const protocolSection = params.protocolSections.find((section) =>
      sameProtocolTreeId(section._id, protocolSectionId)
    );
    return protocolSection?.checklistSectionId
      ? [protocolSection.checklistSectionId]
      : [];
  });

  const nestedChecklistSectionIds = (params.checklistSections ?? []).length
    ? checklistSectionIds.flatMap((checklistSectionId) =>
        collectDescendantIds(params.checklistSections ?? [], checklistSectionId)
      )
    : checklistSectionIds;

  const sectionIdSet = [...protocolSectionIds, ...nestedChecklistSectionIds];

  return params.protocol
    .filter((protocolItem) => {
      const parentSectionId = parentSectionIdFromProtocolItem(
        protocolItem,
        params.checklistItems ?? []
      );
      return sectionIdSet.some((sectionId) =>
        sameProtocolTreeId(sectionId, parentSectionId)
      );
    })
    .map((protocolItem) => protocolItem._id);
}

function resolveChecklistSectionRootId(
  sectionKeyId: string,
  protocolSections: ProtocolSectionTreeNode[]
): string {
  const protocolSection = protocolSections.find(
    (section) =>
      sameProtocolTreeId(section._id, sectionKeyId) ||
      sameProtocolTreeId(section.checklistSectionId, sectionKeyId)
  );

  return protocolSection?.checklistSectionId ?? sectionKeyId;
}

export function collectChecklistItemIdsInSectionTree(params: {
  sectionKeyId: string;
  protocolSections?: ProtocolSectionTreeNode[];
  checklistItems: ChecklistTreeNode[];
  checklistSections?: ChecklistTreeNode[];
}): string[] {
  const rootChecklistSectionId = resolveChecklistSectionRootId(
    params.sectionKeyId,
    params.protocolSections ?? []
  );
  const checklistSectionIds = collectDescendantIds(
    params.checklistSections ?? [],
    rootChecklistSectionId
  );

  return params.checklistItems
    .filter((item) =>
      checklistSectionIds.some((sectionId) =>
        sameProtocolTreeId(sectionId, item.parentSectionId)
      )
    )
    .map((item) => item._id);
}
